import { useState } from 'react';
import { Box, IconButton, InputBase } from '@mui/material';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SaAccountMenu } from './SaAccountMenu';
import { SaImpersonationLauncherDialog } from './SaImpersonationLauncherDialog';
import { useCustomerSearch, SEARCH_MIN_QUERY_LENGTH } from './useCustomerSearch';

interface SaTopBarProps {
  isMobile: boolean;
  onMenuClick: () => void;
}

/**
 * The console's GLOBAL bar (prototype `SaTopBar`): a persistent "Internal — all
 * customers" scope pill, the cross-customer command search, the cross-customer
 * "View as user" launcher ({@link SaImpersonationLauncherDialog}), and the
 * DecisionPoint operator account menu.
 */
export function SaTopBar({ isMobile, onMenuClick }: SaTopBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [viewAsUserOpen, setViewAsUserOpen] = useState(false);
  const { data: matches = [], isFetching } = useCustomerSearch(query);
  const q = query.trim();
  const showResults = searchOpen && q.length >= SEARCH_MIN_QUERY_LENGTH;

  const pickCustomer = () => {
    setSearchOpen(false);
    setQuery('');
    navigate('/site/customers');
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          px: { xs: 2, md: '24px' },
          py: '11px',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {isMobile && (
          <IconButton
            edge="start"
            onClick={onMenuClick}
            aria-label={t('shell.openNavigation')}
            sx={{ flexShrink: 0 }}
          >
            <MenuOutlined />
          </IconButton>
        )}

        {/* persistent internal-scope marker */}
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.875,
            flexShrink: 0,
            px: 1.5,
            py: '6px',
            borderRadius: 999,
            bgcolor: 'rgba(0,71,133,0.12)',
            border: '1px solid rgba(0,71,133,0.30)',
            color: 'primary.dark',
            fontFamily: (theme) => theme.typography.button.fontFamily,
            fontWeight: 700,
            fontSize: 11.5,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          <PublicOutlined sx={{ fontSize: 13 }} />
          {t('siteAdmin:shell.internalPill')}
        </Box>

        {/* cross-customer command search */}
        <Box sx={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: 440 }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <SearchOutlined
              sx={{
                position: 'absolute',
                left: 12,
                fontSize: 18,
                color: 'text.secondary',
                pointerEvents: 'none',
              }}
            />
            <InputBase
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              placeholder={t('siteAdmin:shell.searchPlaceholder')}
              inputProps={{ 'aria-label': t('siteAdmin:shell.searchPlaceholder') }}
              sx={{
                width: '100%',
                height: 40,
                pl: '38px',
                pr: 1.5,
                borderRadius: '9px',
                bgcolor: 'paperAlt',
                border: '1px solid',
                borderColor: searchOpen ? 'primary.main' : 'divider',
                fontSize: 13.5,
                transition: 'border-color 120ms',
              }}
            />
          </Box>
          {showResults && (
            <>
              <Box
                onClick={() => setSearchOpen(false)}
                sx={{ position: 'fixed', inset: 0, zIndex: 39 }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.75,
                  zIndex: 40,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  boxShadow: '0 14px 44px rgba(4,29,51,0.20)',
                  overflow: 'hidden',
                  maxHeight: 360,
                  overflowY: 'auto',
                }}
              >
                {matches.length === 0 ? (
                  <Box
                    sx={{
                      px: 2,
                      py: 2.5,
                      textAlign: 'center',
                      fontSize: 13,
                      color: 'text.secondary',
                    }}
                  >
                    {isFetching
                      ? t('siteAdmin:shell.searchHint')
                      : t('siteAdmin:shell.searchEmpty', { query: q })}
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        px: 1.75,
                        pt: 1,
                        pb: 0.5,
                        bgcolor: 'paperAlt',
                        fontFamily: (theme) => theme.typography.button.fontFamily,
                        fontWeight: 700,
                        fontSize: 10,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                      }}
                    >
                      {t('siteAdmin:shell.searchGroup.customers')}
                    </Box>
                    {matches.map((c) => (
                      <Box
                        key={c.id}
                        component="button"
                        type="button"
                        onClick={pickCustomer}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          width: '100%',
                          border: 'none',
                          bgcolor: 'transparent',
                          cursor: 'pointer',
                          px: 1.75,
                          py: 1,
                          textAlign: 'left',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                          {c.name}
                        </Box>
                        <Box sx={{ fontSize: 11.5, color: 'text.secondary' }}>{c.supportEmail}</Box>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: 'auto', flexShrink: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setViewAsUserOpen(true)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.875,
              px: 1.75,
              py: '8px',
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
            }}
          >
            <VisibilityOutlined sx={{ fontSize: 15, color: 'primary.dark' }} />
            {t('siteAdmin:shell.viewAsUser')}
          </Box>
          <Box sx={{ width: '1px', height: 28, bgcolor: 'divider' }} />
          <SaAccountMenu />
        </Box>
      </Box>
      <SaImpersonationLauncherDialog
        open={viewAsUserOpen}
        onClose={() => setViewAsUserOpen(false)}
      />
    </>
  );
}
